import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { LOCAL_TRANSLATIONS } from '../i18n/localTranslations';

// Create the context
const LanguageContext = createContext();

// Available languages
export const LANGUAGES = {
  en: { code: 'en', name: 'English', nativeName: 'English' },
  ta: { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  si: { code: 'si', name: 'Sinhala', nativeName: 'සිංහල' }
};

// Language direction (for future RTL support)
export const getLanguageDirection = (languageCode) => {
  return languageCode === 'ar' || languageCode === 'he' ? 'rtl' : 'ltr';
};

const interpolate = (template, params) => {
  if (typeof template !== 'string' || !params || typeof params !== 'object') {
    return template;
  }

  return template.replace(/\{\{\s*([^{}\s]+)\s*\}\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      return value === null || value === undefined ? '' : String(value);
    }
    return match;
  });
};

// Custom hook for language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get saved language from localStorage or default to English
    const savedLanguage = localStorage.getItem('ravana-language');
    return savedLanguage && Object.keys(LANGUAGES).includes(savedLanguage)
      ? savedLanguage
      : 'en';
  });

  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Load translations for the current language
  const loadTranslations = useCallback(async (languageCode) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('languages')
        .select('id')
        .eq('code', languageCode)
        .single();

      if (error || !data) {
        setTranslations({});
        return;
      }

      const { data: translationsData, error: translationsError } = await supabase
        .from('translations')
        .select(`
          translated_value,
          translation_keys!inner(
            key_name,
            default_value
          )
        `)
        .eq('language_id', data.id);

      if (translationsError) {
        console.error('Error loading translations:', translationsError);
        setTranslations({});
        return;
      }

      const translationsMap = {};
      if (translationsData) {
        translationsData.forEach(translation => {
          const { key_name, default_value } = translation.translation_keys;
          translationsMap[key_name] = translation.translated_value || default_value;
        });
      }

      setTranslations(translationsMap);
    } catch (error) {
      console.error('Error loading translations:', error);
      setTranslations({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load available languages
  const loadAvailableLanguages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('languages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error || !data) {
        setAvailableLanguages(Object.values(LANGUAGES));
        return;
      }

      setAvailableLanguages(data);
    } catch (error) {
      console.error('Error loading languages:', error);
      setAvailableLanguages(Object.values(LANGUAGES));
    }
  }, []);

  // Change language
  const changeLanguage = useCallback(async (languageCode) => {
    if (!Object.keys(LANGUAGES).includes(languageCode)) {
      console.warn(`Unsupported language: ${languageCode}`);
      return;
    }

    // Save to localStorage
    localStorage.setItem('ravana-language', languageCode);

    // Update state
    setCurrentLanguage(languageCode);

    // Load translations for new language
    await loadTranslations(languageCode);

    // Update HTML lang attribute
    document.documentElement.lang = languageCode;
    document.documentElement.dir = getLanguageDirection(languageCode);
  }, [loadTranslations]);

  // Get translation for a key
  const t = useCallback((key, fallback = '', params = {}) => {
    const fallbackValue = fallback || key;

    if (isLoading) {
      return interpolate(fallbackValue, params);
    }

    if (translations[key] != null) {
      return interpolate(translations[key], params);
    }

    const localMap = LOCAL_TRANSLATIONS[currentLanguage] || {};
    if (localMap[key] != null) {
      return interpolate(localMap[key], params);
    }

    const englishFallback = LOCAL_TRANSLATIONS.en[key];
    if (englishFallback != null) {
      return interpolate(englishFallback, params);
    }

    return interpolate(fallbackValue, params);
  }, [translations, isLoading, currentLanguage]);

  // Get country name translation
  const getCountryName = useCallback(async (countryCode) => {
    try {
      const { data, error } = await supabase
        .from('country_translations')
        .select('translated_name')
        .eq('country_id', (await supabase
          .from('countries')
          .select('id')
          .eq('code', countryCode)
          .single()
        ).data?.id)
        .eq('language_id', (await supabase
          .from('languages')
          .select('id')
          .eq('code', currentLanguage)
          .single()
        ).data?.id)
        .single();

      if (error || !data) {
        // Fallback to English country name
        const { data: countryData } = await supabase
          .from('countries')
          .select('name')
          .eq('code', countryCode)
          .single();

        return countryData?.name || countryCode;
      }

      return data.translated_name;
    } catch (error) {
      console.error('Error getting country translation:', error);
      return countryCode;
    }
  }, [currentLanguage]);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      await loadAvailableLanguages();
      await loadTranslations(currentLanguage);

      // Set initial HTML attributes
      document.documentElement.lang = currentLanguage;
      document.documentElement.dir = getLanguageDirection(currentLanguage);
    };

    initialize();
  }, [currentLanguage, loadAvailableLanguages, loadTranslations]);

  // Value object provided by context
  const value = {
    currentLanguage,
    availableLanguages,
    isLoading,
    changeLanguage,
    t,
    getCountryName,
    getLanguageDirection: () => getLanguageDirection(currentLanguage)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
